import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { Department } from "@/components/acl/group-uesr-list/Departments/values/Department";
import { fetchSolutionLinks } from "@/components/solutions";
import type { Domains } from "@/direct-access";
import type { DirectTalksType } from "@/direct-restapi-types";
import { IndexedDB } from "@/indexeddb";
import {
  isAndroid,
  isBrowserApp,
  isDesktopApp,
  isIos,
  isMobile,
} from "@/mobile";
import { Domain, type Solution, type User } from "@/model";
import { normalizeTalk, type Talk } from "@/model/talk";
import sentry from "@/sentry";
import {
  type AuthorityGroupPolicy,
  CreateTopicAuthorityGroupPolicyDefault,
  ShowReactionDatePolicyDefault,
  type ShowReactionDateType,
  ShowReactionDateTypeDefault,
  UpdateTopicAuthorityGroupPolicyDefault,
} from "@/support-acl-types";
import {
  type AttachmentFileTypes,
  AttachmentFileTypesDefault,
  AttachmentFileTypesNone,
  type AttachmentPolicy,
  AttachmentPolicyDefault,
  DefaultAllowAttachmentTypeByClient,
  type DownloadDeviceTypes,
  DownloadDeviceTypesNone,
  type DownloadPolicy,
  DownloadPolicyDefault,
} from "@/suppport-attachment-types";
import { AppKeys } from "../direct-app-config";
import { useAppStore } from "./app-store";
import DomainDao from "./domain-dao";
import { useUserStore } from "./user-store";

export type DomainRoleType = {
  role_id: number;
  type: number;
  name: string;
};

export interface IDomainSetting {
  // 話題 作成/更新 権限
  create_topic_authority_group_policy: AuthorityGroupPolicy;
  update_topic_authority_group_policy: AuthorityGroupPolicy;

  // ファイル添付設定
  allow_attachment_type: number;
  attachment_policy: AttachmentPolicy;
  attachment_file_type: AttachmentFileTypes;
  upload_path: string;
  allow_attachment_type_by_client: {
    android: number;
    bot: number;
    desktop: number;
    ios: number;
    oidc_client: number;
    web: number;
  };

  // ダウンロード設定
  download_policy: DownloadPolicy;
  allow_save_attachments: DownloadDeviceTypes;
  download_device_type: DownloadDeviceTypes;

  // いいね！の日時表示設定
  show_reaction_date_policy: boolean;
  show_reaction_date_type: ShowReactionDateType;

  // オープントーク設定
  open_talk_enabled: boolean;

  // 基本設定
  domainId: string;
  domainName: string;
  pro: boolean;
  alpha: boolean;
  beta: boolean;
  appKeys: AppKeys[];
  maxFileSize: number;
  domainRole?: DomainRoleType;
  departments?: Department[];
  solutions: Solution[];
  directApps: Solution[];
  talks?: Talk[];
}

type DomainSettings = Record<string, IDomainSetting>;

export type SetPayload = {
  allow_attachment_type: number;
  allow_attachment_type_by_client: {
    android: number;
    bot: number;
    desktop: number;
    ios: number;
    oidc_client: number;
    web: number;
  };
  create_topic_authority_group_policy?: AuthorityGroupPolicy;
  update_topic_authority_group_policy?: AuthorityGroupPolicy;
  attachment_policy?: AttachmentPolicy;
  attachment_file_types?: AttachmentFileTypes;
  upload_path?: string;
  allow_save_attachments: DownloadDeviceTypes;
  download_policy?: DownloadPolicy;
  download_device_type?: DownloadDeviceTypes;
  show_reaction_date_policy?: boolean;
  show_reaction_date_type?: ShowReactionDateType;
  open_talk_enabled: boolean;
  domainId: string;
  domainName: string;
  domainRole?: DomainRoleType;
  solution_ids?: (number | null)[];
  direct_app_keys?: AppKeys[];
  maxFileSize?: number;
  solutions?: Solution[];
  directApps?: Solution[];
};

export type SetAttachmentPolicyPayload = {
  domainId: string;
  attachmentPolicy: AttachmentPolicy;
  attachmentFileTypes: AttachmentFileTypes;
  downloadPolicy: DownloadPolicy;
};
export type SetDomainPolicyPayload = {
  domainId: string;
  createTopicAuthorityGroupPolicy: AuthorityGroupPolicy;
  updateTopicAuthorityGroupPolicy: AuthorityGroupPolicy;
  attachmentPolicy: AttachmentPolicy;
  attachmentFileType: AttachmentFileTypes;
  downloadPolicy: DownloadPolicy;
  downloadDeviceType: DownloadDeviceTypes;
  showReactionDatePolicy: boolean;
  showReactionDateType: ShowReactionDateType;
};

const unitConversion = (size: number): string => {
  let conversion = size;
  if (Math.floor(conversion / 1000) < 1) return `${conversion}B`;
  conversion = Math.floor(conversion / 1000);
  if (Math.floor(conversion / 1000) < 1) return `${conversion}kB`;
  conversion = Math.floor(conversion / 1000);
  if (Math.floor(conversion / 1000) < 1) return `${conversion}MB`;
  conversion = Math.floor(conversion / 1000);
  return `${conversion}GB`;
};

export type ClientType = "ios" | "android" | "web" | "desktop" | "unknown";

const isClientType = (device: string, invoker: string): ClientType => {
  if (isMobile(device)) {
    if (isIos(device)) {
      return "ios";
    } else if (isAndroid(device)) {
      return "android";
    } else {
      return "unknown";
    }
  } else {
    if (isDesktopApp(invoker)) {
      return "desktop";
    } else if (isBrowserApp(invoker)) {
      return "web";
    } else {
      return "unknown";
    }
  }
};

const DefaultDomainSetting: Readonly<IDomainSetting> = {
  create_topic_authority_group_policy: CreateTopicAuthorityGroupPolicyDefault,
  update_topic_authority_group_policy: UpdateTopicAuthorityGroupPolicyDefault,
  allow_attachment_type: 1,
  allow_attachment_type_by_client: DefaultAllowAttachmentTypeByClient,
  attachment_policy: AttachmentPolicyDefault,
  attachment_file_type: AttachmentFileTypesDefault,
  upload_path: "reads",
  allow_save_attachments: {
    web: true,
    ios: true,
    android: true,
    desktop: true,
  },
  download_policy: DownloadPolicyDefault,
  download_device_type: DownloadDeviceTypesNone,
  show_reaction_date_policy: ShowReactionDatePolicyDefault,
  show_reaction_date_type: ShowReactionDateTypeDefault,
  open_talk_enabled: false,
  domainId: "",
  domainName: "",
  domainRole: undefined,
  departments: undefined,
  pro: false,
  alpha: false,
  beta: false,
  appKeys: [],
  maxFileSize: 10000000,
  solutions: [],
  directApps: [],
  talks: [],
};

/**
 * domain-store が app-store から読むメンバのみを表す最小インタフェース。
 * useAppStore() の戻り値を本型へキャストすることで、
 * app-store ↔ domain-store の setup-store 型の循環推論を断ち切る（型のみ・挙動不変）。
 */
interface AppStoreSlice {
  domainId: string;
  allowFeature: { [keyword: string]: string };
  device: string;
  invokerapp: string;
}

export const useDomainStore = defineStore("domains", () => {
  // 循環推論回避のため、app-store は最小スライス型で受ける。
  const appStore = (): AppStoreSlice => useAppStore() as AppStoreSlice;

  const domains = ref<Domains>([]);
  const domainSettings = ref<DomainSettings>({});

  // domainSettingを返す(該当するdomainIdがない場合はdefault値を返す)
  const getDominSetting = (domainId: string): IDomainSetting => {
    const domainSetting = domainSettings.value[domainId];
    return domainSetting ?? DefaultDomainSetting;
  };

  const currentDomainId = (): string => appStore().domainId;

  // --- 基本 getters ---
  const directDomains = computed<Domains>(() => domains.value);
  const get = computed<IDomainSetting>(() =>
    getDominSetting(currentDomainId()),
  );
  const isPro = computed<boolean>(() => getDominSetting(currentDomainId()).pro);
  const isAlpha = computed<boolean>(
    () => getDominSetting(currentDomainId()).alpha,
  );
  const isBeta = computed<boolean>(
    () => getDominSetting(currentDomainId()).beta,
  );

  // allowFeature の機能管理(domain-store ローカル簡易版)
  const allowFeatureLocal = (keyword: string): boolean => {
    const allowFeature = appStore().allowFeature;
    if (!allowFeature || !(keyword in allowFeature)) return true;
    switch (allowFeature[keyword]) {
      case "pro":
        return isPro.value;
      case "alpha":
        return isAlpha.value;
      case "beta":
        return isBeta.value;
      case "dev":
        return false;
      default:
        return true;
    }
  };

  // 組織設定として、ダウンロードが許可されているかどうかを返す
  const allowDownload = (clientType: ClientType): boolean => {
    const domainId = currentDomainId();
    const isMobileClient = () =>
      clientType === "ios" || clientType === "android";
    if (allowFeatureLocal("mobile-download")) {
      // モバイル時ダウンロード許可
    } else {
      if (isMobileClient()) return false; // モバイルは現時点ではダウンロード不可
    }
    const domainSetting = getDominSetting(domainId);
    switch (domainSetting.download_policy) {
      case "ALLOW": {
        switch (clientType) {
          case "ios":
            return (
              domainSetting.download_device_type.ios &&
              domainSetting.allow_save_attachments.ios === true
            );
          case "android":
            return (
              domainSetting.download_device_type.android &&
              domainSetting.allow_save_attachments.android === true
            );
          case "web":
            return domainSetting.download_device_type.web;
          case "desktop":
            return domainSetting.download_device_type.desktop;
          default: {
            const deviceKeys = Object.keys(
              domainSetting.allow_save_attachments,
            ) as (keyof DownloadDeviceTypes)[];
            return deviceKeys.every(
              (key) => domainSetting.allow_save_attachments[key] === true,
            );
          }
        }
      }
      case "DENY":
        return false;
      case "FOLLOW_DIRECT": {
        switch (clientType) {
          case "ios":
            return domainSetting.allow_save_attachments.ios === true;
          case "android":
            return domainSetting.allow_save_attachments.android === true;
          case "web":
            return domainSetting.allow_save_attachments.web === true;
          case "desktop":
            return domainSetting.allow_save_attachments.desktop === true;
          default: {
            const deviceKeys = Object.keys(
              domainSetting.allow_save_attachments,
            ) as (keyof DownloadDeviceTypes)[];
            return deviceKeys.every(
              (key) => domainSetting.allow_save_attachments[key] === true,
            );
          }
        }
      }
      default:
        return false;
    }
  };

  const downloadTypeList = (): string[] => {
    const app = appStore();
    const clientType = isClientType(app.device, app.invokerapp);
    if (allowDownload(clientType)) {
      return ["image", "pdf", "movie", "office"];
    } else {
      return [];
    }
  };

  const getCreateTopicAuthorityGroupPolicy = computed<AuthorityGroupPolicy>(
    () =>
      getDominSetting(currentDomainId()).create_topic_authority_group_policy,
  );
  const getUpdateTopicAuthorityGroupPolicy = computed<AuthorityGroupPolicy>(
    () =>
      getDominSetting(currentDomainId()).update_topic_authority_group_policy,
  );
  const getAttachmentPolicy = computed(
    () => getDominSetting(currentDomainId()).attachment_policy,
  );
  const getAttachmentFileTypes = computed(
    () => getDominSetting(currentDomainId()).attachment_file_type,
  );
  const getAllowAttachmentType = computed<AttachmentFileTypes>(() => {
    const app = appStore();
    const domainSetting = getDominSetting(currentDomainId());
    switch (domainSetting.attachment_policy) {
      case "ALLOW":
        return domainSetting.attachment_file_type;
      case "DENY":
        return AttachmentFileTypesNone;
    }
    const client = isClientType(app.device, app.invokerapp);
    let allowAttachmentType = 1;
    // FeatureFlag 添付ファイル設定をclient別で判定
    if (allowFeatureLocal("attachments-setting-by-client")) {
      switch (client) {
        case "ios":
          allowAttachmentType =
            domainSetting.allow_attachment_type_by_client.ios;
          break;
        case "android":
          allowAttachmentType =
            domainSetting.allow_attachment_type_by_client.android;
          break;
        case "web":
          allowAttachmentType =
            domainSetting.allow_attachment_type_by_client.web;
          break;
        case "desktop":
          allowAttachmentType =
            domainSetting.allow_attachment_type_by_client.desktop;
          break;
        default:
          allowAttachmentType = domainSetting.allow_attachment_type;
          break;
      }
    } else {
      allowAttachmentType = domainSetting.allow_attachment_type;
    }

    switch (allowAttachmentType) {
      case 0:
        return AttachmentFileTypesNone;
      case 1:
        return { image: true, movie: true, pdf: true, office: true };
      case 2:
        return { image: true, movie: false, pdf: false, office: false };
      case 3:
        return { image: true, movie: true, pdf: false, office: false };
      case 4:
        return { image: true, movie: true, pdf: false, office: false };
      default:
        return { image: true, movie: true, pdf: true, office: true };
    }
  });
  const getUploadPath = computed<string>(
    () => getDominSetting(currentDomainId()).upload_path,
  );
  const getDownloadPolicy = computed(
    () => getDominSetting(currentDomainId()).download_policy,
  );
  const getDownloadFileTypes = computed(
    () => getDominSetting(currentDomainId()).download_device_type,
  );
  const getAllowDownload = computed<boolean>(() => {
    const app = appStore();
    const clientType = isClientType(app.device, app.invokerapp);
    return allowDownload(clientType);
  });
  const getDownloadTypeList = computed(() => downloadTypeList());
  const getDomainId = computed<string>(
    () => getDominSetting(currentDomainId()).domainId,
  );
  const getDomainName = computed<string>(
    () => getDominSetting(currentDomainId()).domainName,
  );
  const getDomainRole = computed<DomainRoleType | undefined>(
    () => getDominSetting(currentDomainId()).domainRole,
  );
  const getDepartments = computed<Department[] | undefined>(
    () => getDominSetting(currentDomainId()).departments,
  );
  const appKeys = computed<AppKeys[]>(
    () => getDominSetting(currentDomainId()).appKeys,
  );
  const isEnable = (domainId: string): boolean => {
    if (!domainId) return false;
    const domainSetting = getDominSetting(domainId);
    return (
      domainSetting.domainId === domainId &&
      0 <= domainSetting.appKeys.indexOf(AppKeys.FORUM)
    );
  };
  const getClientType = computed<ClientType>(() => {
    const app = appStore();
    return isClientType(app.device, app.invokerapp);
  });
  const getAllowSaveAttachments = computed<DownloadDeviceTypes>(() => {
    return {
      web: allowDownload("web"),
      ios: allowDownload("ios"),
      android: allowDownload("android"),
      desktop: allowDownload("desktop"),
    };
  });
  const getMaxFileSize = computed<number>(
    () => getDominSetting(currentDomainId()).maxFileSize,
  );
  const getFileSizeUnit = computed<string>(() =>
    unitConversion(getDominSetting(currentDomainId()).maxFileSize),
  );
  const getSolutions = (domainId: string): Solution[] =>
    getDominSetting(domainId).solutions || [];
  const getDirectApps = (domainId: string): Solution[] =>
    getDominSetting(domainId).directApps || [];
  const getShowReactionDatePolicy = computed(
    () => getDominSetting(currentDomainId()).show_reaction_date_policy,
  );
  const getShowReactionDateType = computed(
    () => getDominSetting(currentDomainId()).show_reaction_date_type,
  );
  const getTalks = computed<Talk[]>(
    () => getDominSetting(currentDomainId()).talks || [],
  );
  const isEnableOpenTalk = computed<boolean>(
    () => getDominSetting(currentDomainId()).open_talk_enabled,
  );

  // --- 内部更新 (旧 mutations 相当) ---
  const setMutation = (payload: SetPayload): void => {
    const domainId = payload.domainId;
    const domainSetting: IDomainSetting = {
      create_topic_authority_group_policy:
        payload.create_topic_authority_group_policy ??
        CreateTopicAuthorityGroupPolicyDefault,
      update_topic_authority_group_policy:
        payload.update_topic_authority_group_policy ??
        UpdateTopicAuthorityGroupPolicyDefault,
      allow_attachment_type: payload.allow_attachment_type,
      allow_attachment_type_by_client: payload.allow_attachment_type_by_client,
      attachment_policy:
        payload.attachment_policy ?? DefaultDomainSetting.attachment_policy,
      attachment_file_type:
        payload.attachment_file_types ??
        DefaultDomainSetting.attachment_file_type,
      upload_path: payload.upload_path ?? DefaultDomainSetting.upload_path,
      allow_save_attachments: payload.allow_save_attachments,
      download_policy:
        payload.download_policy ?? DefaultDomainSetting.download_policy,
      download_device_type:
        payload.download_device_type ??
        DefaultDomainSetting.download_device_type,
      show_reaction_date_policy:
        payload.show_reaction_date_policy ?? ShowReactionDatePolicyDefault,
      show_reaction_date_type:
        payload.show_reaction_date_type ?? ShowReactionDateTypeDefault,
      open_talk_enabled: payload.open_talk_enabled,
      domainId: payload.domainId,
      domainName: payload.domainName,
      domainRole: payload.domainRole ?? DefaultDomainSetting.domainRole,
      pro: (payload.solution_ids || []).includes(27),
      alpha: (payload.solution_ids || []).includes(28),
      beta: (payload.solution_ids || []).includes(29),
      appKeys: payload.direct_app_keys ?? DefaultDomainSetting.appKeys,
      maxFileSize: payload.maxFileSize ?? DefaultDomainSetting.maxFileSize,
      solutions: payload.solutions ?? DefaultDomainSetting.solutions,
      directApps: payload.directApps ?? DefaultDomainSetting.directApps,
    };

    const origin = getDominSetting(domainId);
    if (
      origin.domainId.length === 0 ||
      (origin.domainId.length > 0 && origin.domainId === domainId)
    ) {
      domainSettings.value = {
        ...domainSettings.value,
        [domainId]: domainSetting,
      };
    }
  };

  const setDomainsMutation = (payload: Domains): void => {
    domains.value = payload;
  };

  const setDepartmentsMutation = (payload: {
    domainId: string;
    departments: Department[] | undefined;
  }): void => {
    if (payload.departments !== undefined) {
      const origin = getDominSetting(payload.domainId);
      const domainSetting: IDomainSetting = {
        ...origin,
        departments: payload.departments,
      };
      domainSettings.value = {
        ...domainSettings.value,
        [payload.domainId]: domainSetting,
      };
    }
  };

  const setDepartmentsRecordMutation = (
    payload: Record<string, Department[]>,
  ): void => {
    for (const [domainId, departments] of Object.entries(payload)) {
      if (departments !== undefined) {
        const origin = getDominSetting(domainId);
        const domainSetting: IDomainSetting = {
          ...origin,
          departments: departments,
        };
        domainSettings.value = {
          ...domainSettings.value,
          [domainId]: domainSetting,
        };
      }
    }
  };

  const setDomainPolicyMutation = (payload: SetDomainPolicyPayload): void => {
    const create_topic_authority_group_policy =
      payload?.createTopicAuthorityGroupPolicy ??
      CreateTopicAuthorityGroupPolicyDefault;
    const update_topic_authority_group_policy =
      payload?.updateTopicAuthorityGroupPolicy ??
      UpdateTopicAuthorityGroupPolicyDefault;
    const attachment_policy =
      payload.attachmentPolicy ?? AttachmentPolicyDefault;
    const attachment_file_type =
      payload.attachmentFileType ?? AttachmentFileTypesDefault;
    const download_policy = payload.downloadPolicy ?? DownloadPolicyDefault;
    const download_device_type =
      payload.downloadDeviceType ?? DownloadDeviceTypesNone;
    const show_reaction_date_policy =
      payload.showReactionDatePolicy ?? ShowReactionDatePolicyDefault;
    const show_reaction_date_type =
      payload.showReactionDateType ?? ShowReactionDateTypeDefault;
    const origin = getDominSetting(payload.domainId);
    const domainSetting: IDomainSetting = {
      ...origin,
      create_topic_authority_group_policy,
      update_topic_authority_group_policy,
      attachment_policy,
      attachment_file_type,
      download_policy,
      download_device_type,
      show_reaction_date_policy,
      show_reaction_date_type,
    };
    domainSettings.value = {
      ...domainSettings.value,
      [payload.domainId]: domainSetting,
    };
  };

  const setSolutionsMutation = (payload: {
    domainId: string;
    val: Solution[];
  }): void => {
    const sortedValue = payload.val.sort((l, r) => l.priority - r.priority);
    const origin = getDominSetting(payload.domainId);
    const domainSetting: IDomainSetting = { ...origin, solutions: sortedValue };
    domainSettings.value = {
      ...domainSettings.value,
      [payload.domainId]: domainSetting,
    };
  };

  const setDirectAppsMutation = (payload: {
    domainId: string;
    val: Solution[];
  }): void => {
    const sortedValue = payload.val.sort((l, r) => l.priority - r.priority);
    const origin = getDominSetting(payload.domainId);
    const domainSetting: IDomainSetting = {
      ...origin,
      directApps: sortedValue,
    };
    domainSettings.value = {
      ...domainSettings.value,
      [payload.domainId]: domainSetting,
    };
  };

  const setTalksMutation = (payload: {
    domainId: string;
    talks: Talk[];
  }): void => {
    const origin = getDominSetting(payload.domainId);
    const domainSetting: IDomainSetting = { ...origin, talks: payload.talks };
    domainSettings.value = {
      ...domainSettings.value,
      [payload.domainId]: domainSetting,
    };
  };

  async function setIndexedDB(domainId: string): Promise<void> {
    const domainSetting = getDominSetting(domainId);
    if (domainSetting.departments) {
      await IndexedDB.set("DEPARTMENT", domainId, domainSetting.departments);
    }
  }

  // --- actions ---
  const fetchDomainSettings = async (payload: {
    domainId: string;
  }): Promise<void> => {
    const settings = await DomainDao.read(payload.domainId);
    setDomainPolicyMutation(settings);
  };

  const setDomainSetting = async (payload: SetPayload): Promise<void> => {
    setMutation(payload);

    // 現在と同じ組織のみ掲示板の組織設定を取得
    if (appStore().domainId === payload.domainId) {
      await fetchDomainSettings({ domainId: payload.domainId });
    }
  };

  const setDomains = async (payload: Domains): Promise<void> => {
    setDomainsMutation(payload);
  };

  const setDepartments = async (
    payload: Department[] | undefined,
  ): Promise<void> => {
    const domainId = appStore().domainId;
    setDepartmentsMutation({ domainId, departments: payload });
    await setIndexedDB(domainId);
  };

  const fetchSolutions = async (payload: {
    domainId: string;
  }): Promise<void> => {
    const links = await fetchSolutionLinks(payload.domainId);
    if (links && typeof links === "object") {
      setSolutionsMutation({
        domainId: payload.domainId,
        val: links.solutions || [],
      });
      setDirectAppsMutation({
        domainId: payload.domainId,
        val: links.direct_apps || [],
      });
    } else {
      console.error("API /list-api call error:%O", links);
      if (links !== 401) {
        sentry.sendSentryError(links);
      }
    }
  };

  const setDomainPolicy = async (
    payload: SetDomainPolicyPayload,
  ): Promise<void> => {
    const item = Domain.create({
      domainId: payload.domainId,
      createTopicAuthorityGroupPolicy: payload.createTopicAuthorityGroupPolicy,
      updateTopicAuthorityGroupPolicy: payload.updateTopicAuthorityGroupPolicy,
      attachmentPolicy: payload.attachmentPolicy,
      attachmentFileType: payload.attachmentFileType,
      downloadPolicy: payload.downloadPolicy,
      downloadDeviceType: payload.downloadDeviceType,
      showReactionDatePolicy: payload.showReactionDatePolicy,
      showReactionDateType: payload.showReactionDateType,
    });
    const register = await DomainDao.upsert(item);
    if (register) {
      setDomainPolicyMutation(register);
    }
  };

  const restore = async (): Promise<void> => {
    const departments = await IndexedDB.getAll("DEPARTMENT");
    if (departments) {
      setDepartmentsRecordMutation(departments);
    }
  };

  const setTalks = async (
    payload: DirectTalksType | undefined,
  ): Promise<void> => {
    if (payload) {
      const me = useUserStore().me as User;
      const enableOpenTalk = isEnableOpenTalk.value;
      const talks = payload.map((talk) =>
        normalizeTalk(talk, me.directId, enableOpenTalk),
      );
      setTalksMutation({ domainId: appStore().domainId, talks: talks });
    }
  };

  return {
    domains,
    domainSettings,
    directDomains,
    get,
    isPro,
    isAlpha,
    isBeta,
    getCreateTopicAuthorityGroupPolicy,
    getUpdateTopicAuthorityGroupPolicy,
    getAttachmentPolicy,
    getAttachmentFileTypes,
    getAllowAttachmentType,
    getUploadPath,
    getDownloadPolicy,
    getDownloadFileTypes,
    getAllowDownload,
    getDownloadTypeList,
    getDomainId,
    getDomainName,
    getDomainRole,
    getDepartments,
    appKeys,
    isEnable,
    getClientType,
    getAllowSaveAttachments,
    getMaxFileSize,
    getFileSizeUnit,
    getSolutions,
    getDirectApps,
    getShowReactionDatePolicy,
    getShowReactionDateType,
    getTalks,
    isEnableOpenTalk,
    fetchDomainSettings,
    setDomainSetting,
    setDomains,
    setDepartments,
    fetchSolutions,
    setDomainPolicy,
    restore,
    setTalks,
  };
});

export default useDomainStore;
